/**
 * Carrossel de vídeos — slides largura fixa; só o centro em destaque (scale) + autoplay.
 * Laterais: capa (poster) quando pausado; sem resetar currentTime (evita flash preto).
 */
class VideoTestimonialsCarousel extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this.viewport = this.querySelector('[data-viewport]');
    this.track = this.querySelector('[data-track]');
    this.slides = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-slide]')]);
    if (!this.viewport || !this.track || this.slides.length === 0) return;

    this._initialized = true;
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.activeIndex = Math.max(
      0,
      this.slides.findIndex((s) => s.classList.contains('is-active'))
    );
    if (this.activeIndex < 0) {
      this.activeIndex = Math.min(Math.floor(this.slides.length / 2), this.slides.length - 1);
    }
    this._playbackIndex = -1;
    this.userUnmuted = false;
    this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this._scrollMode = window.matchMedia('(max-width: 989px)').matches;
    this._touchGesture = { moved: false };

    if (this._scrollMode) {
      this.classList.add('video-testimonials__carousel--scroll');
      this.viewport.classList.add('video-testimonials__viewport--scroll');
      this.track.style.transform = 'none';
      this._bindTouchGestureGuard(this.viewport);
      this._bindScrollDrag(this.viewport);
    }

    this.prevBtn?.addEventListener('click', () => this.go(-1));
    this.nextBtn?.addEventListener('click', () => this.go(1));

    this.slides.forEach((slide, i) => {
      slide.querySelector('[data-play]')?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (i !== this.activeIndex) this.setActive(i, true);
        else this.togglePlay(slide);
      });
      slide.querySelector('[data-mute]')?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (i !== this.activeIndex) this.setActive(i, true);
        this.toggleMute();
      });
      if (!this._scrollMode) {
        slide.addEventListener('click', () => {
          if (i !== this.activeIndex) this.setActive(i);
        });
      }
    });

    this._onResize = () => {
      const wasScroll = this._scrollMode;
      this._scrollMode = window.matchMedia('(max-width: 989px)').matches;
      if (wasScroll !== this._scrollMode) {
        this.track.style.transform = this._scrollMode ? 'none' : '';
        this.classList.toggle('video-testimonials__carousel--scroll', this._scrollMode);
        this.viewport.classList.toggle('video-testimonials__viewport--scroll', this._scrollMode);
        if (this._scrollMode) {
          this._bindTouchGestureGuard(this.viewport);
          this._bindScrollDrag(this.viewport);
        }
      }
      this.refreshLayout();
    };
    window.addEventListener('resize', this._onResize);

    if (typeof ResizeObserver !== 'undefined' && !this._scrollMode) {
      this._ro = new ResizeObserver(() => this.refreshLayout());
      this._ro.observe(this.viewport);
    }

    if (this._scrollMode) {
      this._scrollSnapTimer = null;
      this._scrollSyncRaf = 0;
      this._onScroll = () => this._handleScrollMove();
      this.viewport.addEventListener('scroll', this._onScroll, { passive: true });
      if ('onscrollend' in this.viewport) {
        this.viewport.addEventListener('scrollend', () => this._snapToNearestSlide(), {
          passive: true,
        });
      }
    } else {
      this._bindSwipe(this.viewport);
    }

    this._io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this._syncPlaybackAt(this.activeIndex, true);
          } else {
            this.pauseAll();
          }
        });
      },
      { threshold: 0.35 }
    );
    this._io.observe(this);

    this._onPageShow = (event) => {
      if (event.persisted) {
        this.scheduleRecenter();
        this.syncVideos();
      }
    };
    window.addEventListener('pageshow', this._onPageShow);

    this._onVisibility = () => {
      if (document.visibilityState === 'visible') {
        this._syncPlaybackAt(this.activeIndex, true);
      } else {
        this.pauseAll();
      }
    };
    document.addEventListener('visibilitychange', this._onVisibility);

    this._setActiveState(this.activeIndex, false);
    if (!this._scrollMode) {
      this.scheduleRecenter();
    } else {
      this._scrollToIndex(this.activeIndex, false);
    }
    this._initAllCovers();
    this._syncPlaybackAt(this.activeIndex, true);
    this.updateMuteButton();
    this._initSlideProductSwatches(this.slides[this.activeIndex]);
    window.MercadaoSwatchesRow?.initAll(this);
  }

  /**
   * @param {HTMLElement} el
   */
  _bindScrollDrag(el) {
    if (el.dataset.vtScrollDrag === '1') return;
    el.dataset.vtScrollDrag = '1';

    let startX = 0;
    let startScroll = 0;
    let dragging = false;
    let pointerId = null;

    const isInteractiveTarget = (target) =>
      Boolean(
        target instanceof Element &&
          target.closest(
            '[data-play],[data-mute],.video-testimonials__product,a,button,input,label,select'
          )
      );

    const endDrag = () => {
      if (!dragging) return;
      dragging = false;
      pointerId = null;
      el.classList.remove('is-dragging');
      this._snapToNearestSlide();
    };

    el.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (isInteractiveTarget(e.target)) return;
      dragging = true;
      pointerId = e.pointerId;
      startX = e.clientX;
      startScroll = el.scrollLeft;
      el.classList.add('is-dragging');
      try {
        el.setPointerCapture(e.pointerId);
      } catch (err) {
        /* ignore */
      }
    });

    el.addEventListener('pointermove', (e) => {
      if (!dragging || e.pointerId !== pointerId) return;
      el.scrollLeft = startScroll - (e.clientX - startX);
      this._scheduleScrollSync();
    });

    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);
    el.addEventListener('lostpointercapture', endDrag);
  }

  /**
   * @returns {number}
   */
  _getCenteredSlideIndex() {
    const vp = this.viewport.getBoundingClientRect();
    const center = vp.left + vp.width / 2;
    let best = 0;
    let bestDist = Infinity;
    this.slides.forEach((slide, i) => {
      const r = slide.getBoundingClientRect();
      const slideCenter = r.left + r.width / 2;
      const d = Math.abs(slideCenter - center);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    return best;
  }

  _scheduleScrollSync() {
    if (!this._scrollMode || this._scrollSyncRaf) return;
    this._scrollSyncRaf = requestAnimationFrame(() => {
      this._scrollSyncRaf = 0;
      this._syncPlaybackAt(this._getCenteredSlideIndex(), false);
    });
  }

  _handleScrollMove() {
    if (!this._scrollMode) return;
    this._scheduleScrollSync();

    if (this.viewport.classList.contains('is-dragging')) return;

    clearTimeout(this._scrollSnapTimer);
    this._scrollSnapTimer = window.setTimeout(() => {
      this._snapToNearestSlide();
    }, 40);
  }

  _snapToNearestSlide() {
    if (!this._scrollMode || this._scrollLock || !this.viewport) return;
    const index = this._getCenteredSlideIndex();
    this._setActiveState(index, false);
    this._scrollToIndex(index, !this._reducedMotion);
    this._syncPlaybackAt(index, true);
    this.updateMuteButton();
  }

  /**
   * @param {HTMLElement} el
   */
  _bindTouchGestureGuard(el) {
    if (el.dataset.vtTouchGuard === '1') return;
    el.dataset.vtTouchGuard = '1';
    let startX = 0;
    let startY = 0;

    el.addEventListener(
      'touchstart',
      (e) => {
        const t = e.touches[0];
        if (!t) return;
        startX = t.clientX;
        startY = t.clientY;
        this._touchGesture.moved = false;
      },
      { passive: true }
    );

    el.addEventListener(
      'touchmove',
      (e) => {
        const t = e.touches[0];
        if (!t) return;
        if (
          Math.abs(t.clientX - startX) > 10 ||
          Math.abs(t.clientY - startY) > 10
        ) {
          this._touchGesture.moved = true;
        }
      },
      { passive: true }
    );
  }

  /**
   * @param {HTMLElement} el
   */
  _bindSwipe(el) {
    this._swipe = { x: 0, active: false };

    const onStart = (x) => {
      this._swipe = { x, active: true };
    };
    const onEnd = (x) => {
      if (!this._swipe.active) return;
      const dx = x - this._swipe.x;
      this._swipe.active = false;
      if (Math.abs(dx) > 36) this.go(dx < 0 ? 1 : -1);
    };

    el.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      onStart(e.clientX);
    });
    el.addEventListener('pointerup', (e) => onEnd(e.clientX));
    el.addEventListener('pointercancel', () => {
      this._swipe.active = false;
    });
  }

  _initAllCovers() {
    this.slides.forEach((slide, i) => {
      const video = slide.querySelector('video');
      const hasPoster = Boolean(slide.querySelector('[data-vt-poster]'));
      if (!video || !hasPoster) return;
      if (i === this.activeIndex) {
        slide.classList.remove('is-cover-visible');
      } else {
        this._setCoverVisible(slide, true);
      }
    });
  }

  /**
   * Autoplay no slide central (sempre mudo até o usuário ativar som).
   * @param {HTMLElement} slide
   * @param {HTMLVideoElement} video
   * @param {HTMLElement | null} playBtn
   */
  _autoplayCenterVideo(slide, video, playBtn) {
    const hasPoster = Boolean(slide.querySelector('[data-vt-poster]'));
    this._primeVideo(video);
    video.muted = !this.userUnmuted;
    if (!this.userUnmuted) {
      video.muted = true;
      video.defaultMuted = true;
    }
    video.setAttribute('autoplay', '');
    video.preload = 'auto';

    if (hasPoster) slide.classList.remove('is-cover-visible');

    const markPlaying = () => {
      slide.classList.add('is-playing');
      playBtn?.classList.add('is-hidden');
    };

    const markCover = () => {
      slide.classList.remove('is-playing');
      if (hasPoster) this._setCoverVisible(slide, true);
      else playBtn?.classList.remove('is-hidden');
    };

    const tryPlay = () => {
      if (document.visibilityState !== 'visible') return;
      if (this.slides[this.activeIndex] !== slide) return;

      const p = video.play();
      if (p?.then) {
        p.then(markPlaying).catch(markCover);
      } else if (!video.paused) {
        markPlaying();
      }
    };

    tryPlay();

    if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      const onReady = () => {
        if (this.slides[this.activeIndex] === slide) tryPlay();
      };
      video.addEventListener('loadeddata', onReady, { once: true });
      video.addEventListener('canplay', onReady, { once: true });
      try {
        video.load();
      } catch (_) {
        /* ignore */
      }
    }
  }

  /**
   * @param {HTMLElement} slide
   * @param {boolean} visible
   */
  _setCoverVisible(slide, visible) {
    const video = slide.querySelector('video');
    const playBtn = slide.querySelector('[data-play]');
    slide.classList.toggle('is-cover-visible', visible);
    slide.classList.toggle('is-playing', false);
    if (visible) {
      if (video && !video.paused) video.pause();
      playBtn?.classList.remove('is-hidden');
    }
  }

  /**
   * @param {number} index
   * @param {boolean} [force]
   */
  _syncPlaybackAt(index, force = false) {
    const len = this.slides.length;
    if (!len) return;
    const next = ((index % len) + len) % len;
    if (!force && next === this._playbackIndex) return;

    this.slides.forEach((slide, i) => {
      if (i === next) return;
      const video = slide.querySelector('video');
      if (video && !video.paused) video.pause();
      if (slide.querySelector('[data-vt-poster]')) {
        this._setCoverVisible(slide, true);
      }
    });

    this._playbackIndex = next;
    this._setActiveState(next, false);

    const slide = this.slides[next];
    const video = slide?.querySelector('video');
    const playBtn = slide?.querySelector('[data-play]');
    const hasPoster = Boolean(slide?.querySelector('[data-vt-poster]'));

    if (!video) {
      this.updateMuteButton();
      this._initSlideProductSwatches(slide);
      return;
    }

    this._autoplayCenterVideo(slide, video, playBtn);

    this.updateMuteButton();
    this._initSlideProductSwatches(slide);
  }

  /**
   * @param {HTMLElement | undefined} slide
   */
  _initSlideProductSwatches(slide) {
    const row = slide?.querySelector('[data-mercadao-swatches-row]');
    if (row instanceof HTMLElement) {
      window.MercadaoSwatchesRow?.initRow(row);
    }
  }

  syncVideos() {
    this._syncPlaybackAt(this.activeIndex, true);
  }

  recenter() {
    this.refreshLayout();
  }

  scheduleRecenter() {
    if (this._scrollMode) return;
    const run = () => this.refreshLayout();
    requestAnimationFrame(() => requestAnimationFrame(run));
    [50, 150, 350].forEach((ms) => window.setTimeout(run, ms));
  }

  refreshLayout() {
    if (this._scrollMode) return;
    this.centerActive(false);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('pageshow', this._onPageShow);
    document.removeEventListener('visibilitychange', this._onVisibility);
    if (this._scrollMode) {
      this.viewport?.removeEventListener('scroll', this._onScroll);
      clearTimeout(this._scrollSnapTimer);
      if (this._scrollSyncRaf) cancelAnimationFrame(this._scrollSyncRaf);
    }
    this._io?.disconnect();
    this._ro?.disconnect();
    this.pauseAll();
    this._initialized = false;
  }

  go(delta) {
    const len = this.slides.length;
    this.setActive((this.activeIndex + delta + len) % len, true);
  }

  /**
   * @param {number} index
   * @param {boolean} [scrollCarousel=false]
   * @param {boolean} [animate=true]
   */
  setActive(index, scrollCarousel = false, animate = true) {
    this._setActiveState(index, animate);
    if (this._scrollMode) {
      if (scrollCarousel) {
        this._scrollToIndex(index, animate && !this._reducedMotion);
      }
    } else {
      this.centerActive(animate && !this._reducedMotion);
    }
    this._syncPlaybackAt(index, true);
    this.updateMuteButton();
  }

  /**
   * @param {number} index
   * @param {boolean} _animate
   */
  _setActiveState(index, _animate) {
    this.activeIndex = index;
    this.slides.forEach((slide, i) => {
      const isActive = i === index;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
  }

  /**
   * @param {number} index
   * @param {boolean} smooth
   */
  _scrollToIndex(index, smooth) {
    const slide = this.slides[index];
    if (!slide || !this.viewport) return;
    this._scrollLock = true;

    const slideLeft = slide.offsetLeft;
    const slideWidth = slide.offsetWidth;
    const vpWidth = this.viewport.clientWidth;
    const maxScroll = Math.max(0, this.viewport.scrollWidth - vpWidth);
    const targetLeft = Math.min(
      maxScroll,
      Math.max(0, slideLeft - (vpWidth - slideWidth) / 2)
    );

    this.viewport.scrollTo({
      left: targetLeft,
      behavior: smooth && !this._reducedMotion ? 'smooth' : 'auto',
    });

    const unlock = () => {
      this._scrollLock = false;
    };
    if (smooth && !this._reducedMotion) {
      window.setTimeout(unlock, 220);
    } else {
      unlock();
    }
  }

  /**
   * @param {boolean} animate
   */
  centerActive(animate) {
    if (this._scrollMode) return;
    const slide = this.slides[this.activeIndex];
    if (!slide || !this.viewport || !this.track) return;

    const viewportRect = this.viewport.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    const viewportCenter = viewportRect.left + viewportRect.width / 2;
    const slideCenter = slideRect.left + slideRect.width / 2;
    const delta = viewportCenter - slideCenter;

    if (Math.abs(delta) < 0.5) return;

    const matrix = new DOMMatrixReadOnly(getComputedStyle(this.track).transform);
    const currentX = matrix.m41 || 0;
    const nextX = currentX + delta;

    if (!animate || this._reducedMotion) {
      this.track.style.transition = 'none';
    } else {
      this.track.style.transition = 'transform 0.28s cubic-bezier(0.22, 1, 0.36, 1)';
    }
    this.track.style.transform = `translateX(${nextX}px)`;

    if (!animate || this._reducedMotion) {
      requestAnimationFrame(() => {
        this.track.style.transition = '';
      });
    }
  }

  /**
   * @param {HTMLVideoElement} video
   */
  _primeVideo(video) {
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('disablepictureinpicture', '');
  }

  pauseAll() {
    this.slides.forEach((slide) => {
      const video = slide.querySelector('video');
      if (video) video.pause();
      if (slide.querySelector('[data-vt-poster]')) {
        this._setCoverVisible(slide, true);
      }
    });
    this._playbackIndex = -1;
  }

  /**
   * @param {HTMLElement} slide
   */
  togglePlay(slide) {
    const video = slide.querySelector('video');
    const playBtn = slide.querySelector('[data-play]');
    const hasPoster = Boolean(slide.querySelector('[data-vt-poster]'));
    if (!video) return;
    this._primeVideo(video);
    if (video.paused) {
      if (hasPoster) slide.classList.remove('is-cover-visible');
      video
        .play()
        ?.then(() => {
          slide.classList.add('is-playing');
          playBtn?.classList.add('is-hidden');
        })
        .catch(() => {
          if (hasPoster) this._setCoverVisible(slide, true);
        });
    } else {
      video.pause();
      slide.classList.remove('is-playing');
      if (hasPoster) this._setCoverVisible(slide, true);
      else playBtn?.classList.remove('is-hidden');
    }
  }

  toggleMute() {
    this.userUnmuted = !this.userUnmuted;
    const video = this.slides[this.activeIndex]?.querySelector('video');
    if (video) video.muted = !this.userUnmuted;
    this.updateMuteButton();
  }

  updateMuteButton() {
    this.slides.forEach((slide) => {
      slide.querySelector('[data-mute]')?.classList.remove('is-unmuted');
    });
    const btn = this.slides[this.activeIndex]?.querySelector('[data-mute]');
    if (!btn) return;
    const on = this.userUnmuted;
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    if (on) btn.classList.add('is-unmuted');
    const labelOn = btn.dataset.labelUnmute || 'Ativar som';
    const labelOff = btn.dataset.labelMute || 'Desativar som';
    btn.setAttribute('aria-label', on ? labelOff : labelOn);
  }
}

if (!customElements.get('video-testimonials-carousel')) {
  customElements.define('video-testimonials-carousel', VideoTestimonialsCarousel);
}

window.MercadaoVideoCarousel = {
  refresh(root = document) {
    root.querySelectorAll('video-testimonials-carousel').forEach((el) => {
      if (typeof el.recenter === 'function') el.recenter();
      else if (typeof customElements?.upgrade === 'function') customElements.upgrade(el);
    });
  },
};
